import {
  a,
  button,
  div,
  fnstate,
  goTo,
  h1,
  pathParameters,
  span
} from '@srfnstack/fntags'
import { ImageViewer } from '../../components/ImageViewer.js'
import { CreditButton } from '../../components/CreditButton.js'
import { lastJobUpdate, extensionId } from '../../state.js'
import { pingExtension, syncPrintifySession } from '../../utils/extension.js'
import { selectValidVariants, getAvailableSizes, getVariantIdsForSizes, getSizesFromVariantIds } from '../../utils/variants.js'
import { MockupPanel } from './MockupPanel.js'
import { MetadataEditor } from './MetadataEditor.js'
import { PublishControls } from './PublishControls.js'
import { fetchDefaultImageModel } from '../../utils/settings.js'

export const ProductReviewDetail = () => {
  const productId = pathParameters().productId
  // Where to go after publish/reject — back to wherever the user came from (e.g. the
  // approval queue) so they can keep reviewing, not always the Product Concepts list.
  const returnTo = new URLSearchParams(window.location.search).get('from') || '/product-review'
  // True when colors/variants were edited but NOT yet re-pushed to Printify — used to
  // block publishing a stale Etsy listing.
  const variantsDirty = fnstate(false)
  const product = fnstate(null)
  const productTypes = fnstate([])
  const selectedAsset = fnstate(null)
  const viewerAsset = fnstate(null)
  const saving = fnstate(false)
  const variants = fnstate([])
  const selectedVariants = fnstate([])
  const mockupsList = fnstate([], m => m.id)
  const mockupCount = fnstate(6)
  const mockupModel = fnstate('bfl_flux2pro')
  fetchDefaultImageModel().then(m => mockupModel(m))
  const showRejectedMockups = fnstate(false)
  // The concepts sharing this artwork (the unframed canvas and its framed
  // sibling), one tab each. Keyed by id for bindChildren.
  const siblings = fnstate([], s => s.id)

  const fetchSiblings = async () => {
    try {
      const res = await fetch(`/api/product-concepts/${productId}/siblings`)
      if (!res.ok) return
      const data = await res.json()
      if (!Array.isArray(data)) return
      const prev = siblings()
      const changed = prev.length !== data.length ||
        data.some((s, i) => s.id !== prev[i]?.id || s.status !== prev[i]?.status || s.is_current !== prev[i]?.is_current)
      if (changed) siblings(data)
    } catch (err) {
      console.error('Error fetching siblings:', err)
    }
  }

  const handleJobUpdate = (event) => {
    if (!event) return
    const isRelevant = event.payload?.product_concept_id?.toString() === productId.toString()
    if (!isRelevant) return
    const isTerminal = event.status === 'COMPLETED' || event.status === 'FAILED'
    // Use job type to decide what to refresh:
    // - Mockup/upscale jobs only update mockupsList (no product.bindAs re-render)
    // - Structural jobs (printify push/finalize, SEO) update the full product state
    const mockupOnly = ['MOCKUP_GENERATE', 'IMAGE_UPSCALE', 'IMAGE_ENHANCE'].includes(event.job_type)
    fetchProduct(true, mockupOnly)
    if (event.job_type === 'PRINTIFY_PUSH' && isTerminal) {
      if (event.status === 'COMPLETED') {
        variantsDirty(false) // colors/variants are now synced to Printify
        window.alert('Product successfully pushed to Printify! You can now verify it there before publishing.').then(() => saving(false))
      } else if (event.status === 'FAILED') {
        const reason = event.failed_reason || ''
        const msg = reason.includes('HTTP 401') || reason.includes('Unauthorized')
          ? 'Your Printify session token has expired. Please update it in Settings and try again.'
          : reason || 'Something went wrong. Please try again.'
        window.alert(`Push to Printify failed — ${msg}`).then(() => saving(false))
      }
    }
  }

  // Store unsubscribe so stale instances self-clean when root element leaves DOM.
  // Without this, every page navigation stacks another subscriber on the global
  // lastJobUpdate state, causing cascading fetchProduct() calls on each job update.
  let rootEl = null
  const unsubRef = { fn: null }
  let lastSeenEvent = null
  unsubRef.fn = lastJobUpdate.subscribe((event) => {
    if (rootEl && !rootEl.isConnected) {
      unsubRef.fn?.()
      unsubRef.fn = null
      return
    }
    // Skip the initial subscribe fire (stale event) and duplicate events
    const eventKey = event ? `${event.job_id}:${event.status}` : null
    if (!event || eventKey === lastSeenEvent) return
    lastSeenEvent = eventKey
    handleJobUpdate(event)
  })

  // Poll for mockup completion so spinners resolve even if WS events are missed.
  let mockupPollTimer = null
  // Optimistic "Generate More Mockups" placeholders: temp rows shown instantly. We
  // keep them until the server's real mockup count catches up past this baseline.
  let optimisticBaseline = 0
  const isOptimistic = (m) => typeof m.id === 'string' && String(m.id).startsWith('pending-')
  const startMockupPolling = () => {
    if (mockupPollTimer) clearInterval(mockupPollTimer)
    fetchProduct(true, true)
    mockupPollTimer = setInterval(() => {
      if (rootEl && !rootEl.isConnected) { clearInterval(mockupPollTimer); mockupPollTimer = null; return }
      fetchProduct(true, true)
      if (!mockupsList().some(m => m.status === 'PENDING')) { clearInterval(mockupPollTimer); mockupPollTimer = null }
    }, 4000)
    setTimeout(() => { if (mockupPollTimer) { clearInterval(mockupPollTimer); mockupPollTimer = null } }, 300000)
  }

  // Fallback only: poll the product while saving=true so a *dropped* WS event
  // doesn't freeze the page forever. It must NOT preempt the WS completion alert on
  // the happy path — so it only unblocks once the status has actually transitioned
  // to a settled (non-transient) state DIFFERENT from where the operation started.
  // (The old check `status !== QUEUED_FOR_PUBLISH` fired instantly for a push, which
  // never uses that status, re-enabling the button before the success alert.)
  const TRANSIENT = ['QUEUED_FOR_PUBLISH', 'PROCESSING']
  let savingPollTimer = null
  let savingStartStatus = null
  saving.subscribe(isSaving => {
    if (savingPollTimer) { clearInterval(savingPollTimer); savingPollTimer = null }
    if (isSaving) {
      savingStartStatus = product()?.status
      savingPollTimer = setInterval(async () => {
        if (rootEl && !rootEl.isConnected) { clearInterval(savingPollTimer); savingPollTimer = null; return }
        if (!product()) return
        const res = await fetch(`/api/product-concepts/${productId}?_=${Date.now()}`)
        if (!res.ok) return
        const data = await res.json()
        const settled = !TRANSIENT.includes(data.status) && data.status !== savingStartStatus
        if (settled) {
          product(data)
          saving(false)
          clearInterval(savingPollTimer); savingPollTimer = null
        }
      }, 8000)
    }
  })

  const fetchVariants = async (blueprintId, productType, artworkAspectRatio, selectedColors, hasExistingSelection) => {
    if (!blueprintId) return
    try {
      const res = await fetch(`/api/system/printify/blueprints/${blueprintId}/variants`)
      if (res.ok) {
        const data = await res.json()
        const allVariants = data.variants || []
        let valid
        if (productType === 't-shirt') {
          valid = allVariants // keep all; UI derives sizes from selected colors
        } else {
          valid = selectValidVariants('canvas wall art', allVariants, artworkAspectRatio)
        }
        variants(valid)
        if (!hasExistingSelection) {
          let ids
          if (productType === 't-shirt') {
            // Don't auto-populate when no colors are known — we'd get ALL variants
            if (selectedColors.length === 0) return
            const availSizes = getAvailableSizes(valid, selectedColors)
            ids = getVariantIdsForSizes(valid, availSizes, selectedColors)
          } else {
            ids = valid.map(v => v.id)
          }
          selectedVariants(ids)
          await fetch(`/api/product-concepts/${productId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { ...product().attributes, selected_variants: ids } })
          })
        }
      }
    } catch (err) {
      console.error('Error fetching variants:', err)
    }
  }

  const fetchProduct = async (silent = false, mockupOnly = false) => {
    try {
      const res = await fetch(`/api/product-concepts/${productId}?_=${Date.now()}`)
      if (res.ok) {
        const data = await res.json()

        // mockupOnly: skip product(data) to avoid tearing down/rebuilding the
        // entire product subtree (Sortable, click handlers, images). Mockup
        // updates flow through mockupsList.bindChildren instead.
        if (!mockupOnly) {
          product(data)
        }

        // If the product is mid-push (e.g. page was reloaded), keep buttons disabled.
        if (data.status === 'QUEUED_FOR_PUBLISH') {
          saving(true)
        }
        selectedVariants(data.attributes?.selected_variants || [])

        const sortedMockups = data.mockups || []
        const order = data.attributes?.mockup_order
        // Tier the display the same way the Etsy upload orders images: product
        // mockups first, the plain artwork second-to-last, the quality-guarantee
        // badge ALWAYS last (so it's never the lead/thumbnail).
        const tierOf = (m) => m.params?.type === 'quality_guarantee' ? 2 : m.params?.type === 'plain_square' ? 1 : 0
        const isHero = (m) => m.params?.type === 'hero'
        const orderArr = (order && Array.isArray(order)) ? order : null
        // Within a tier, honor a manual mockup_order; otherwise the marble hero
        // leads (it's forced to Etsy image #1, the thumbnail). Mirrors the backend.
        const rankOf = (m) => {
          if (orderArr) {
            const i = orderArr.indexOf(m.id)
            if (i !== -1) return i
            return isHero(m) ? -1 : Number.MAX_SAFE_INTEGER // hero added after ordering still leads
          }
          return isHero(m) ? -1 : 0
        }
        sortedMockups.sort((a, b) => {
          const ta = tierOf(a); const tb = tierOf(b)
          if (ta !== tb) return ta - tb
          return rankOf(a) - rankOf(b)
        })
        // Keep optimistic placeholders visible until the server's real rows appear:
        // drop one placeholder for each new real mockup created since we added them.
        const prevMockups = mockupsList()
        const optimistic = prevMockups.filter(isOptimistic)
        if (optimistic.length > 0) {
          const newlyAppeared = Math.max(0, sortedMockups.length - optimisticBaseline)
          sortedMockups.push(...optimistic.slice(newlyAppeared))
        }
        // Only update mockupsList when data actually changed — fnstate has no
        // equality guard, so setting identical data triggers bindChildren reconcile
        // which rebuilds every thumbnail (causing flicker and broken image loads).
        const mockupsChanged = prevMockups.length !== sortedMockups.length ||
          sortedMockups.some((m, i) => m.id !== prevMockups[i]?.id || m.status !== prevMockups[i]?.status || m.file_path !== prevMockups[i]?.file_path || m.rejected !== prevMockups[i]?.rejected)
        if (mockupsChanged) {
          mockupsList(sortedMockups)
        }

        // Auto-start mockup polling when PENDING mockups exist (e.g. initial page load
        // during mockup generation) so spinners resolve even if WS events are missed.
        if (sortedMockups.some(m => m.status === 'PENDING') && !mockupPollTimer) {
          startMockupPolling()
        }

        // Keep selectedAsset in sync with fresh mockup data from the server
        const current = selectedAsset()
        if (current) {
          const updated = sortedMockups.find(m => m.id === current.id)
          if (updated && !updated.rejected) {
            // Only update if data actually changed to avoid re-render flicker
            if (updated.status !== current.status || updated.file_path !== current.file_path || updated.rejected !== current.rejected) {
              selectedAsset({ ...updated, type: 'MOCKUP' })
            }
          } else if (current.type === 'MOCKUP') {
            // Current mockup was rejected/removed — pick next completed one
            const firstActive = sortedMockups.find(m => !m.rejected && m.status !== 'PENDING')
            if (firstActive) selectedAsset({ ...firstActive, type: 'MOCKUP' })
          }
          // If current is UPSCALE and not in mockups, leave it alone
        } else {
          const firstActive = sortedMockups.find(m => !m.rejected)
          if (firstActive) selectedAsset({ ...firstActive, type: 'MOCKUP' })
        }

        if (!silent) {
          fetchSiblings()
          const typeRes = await fetch('/api/system/product-types')
          if (typeRes.ok) {
            const types = await typeRes.json()
            productTypes(types)
            const currentType = types.find(t => t.keyword.toLowerCase() === data.product_type.toLowerCase())
            if (currentType) {
              fetchVariants(currentType.printify_blueprint_id, data.product_type?.toLowerCase(), data.metadata?.aspect_ratio || '1:1', data.attributes?.selected_colors || [], (data.attributes?.selected_variants || []).length > 0)
            }
          }
        }
      }
    } catch (err) {
      console.error('Fetch error:', err)
    }
  }

  const handleUpdate = async (updates, refresh = true) => {
    // Editing colors/variants desyncs from Printify until the next re-push. Only mark
    // dirty when the values ACTUALLY change — not when they're merely spread into an
    // unrelated attributes update (e.g. reordering mockups carries selected_variants
    // along untouched, which must NOT block publish since mockups aren't pushed).
    if (updates.attributes && product()?.printify_product_id) {
      const cur = product().attributes || {}
      const changed = (k) => k in updates.attributes &&
        JSON.stringify(updates.attributes[k] ?? null) !== JSON.stringify(cur[k] ?? null)
      if (changed('selected_variants') || changed('selected_colors')) variantsDirty(true)
    }
    saving(true)
    try {
      const res = await fetch(`/api/product-concepts/${productId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      })
      if (res.ok) {
        if (updates.status && (updates.status === 'QUEUED_FOR_PUBLISH' || updates.status === 'FAILED')) {
          await window.alert(`Product status updated to ${updates.status}`)
          goTo(returnTo)
        } else if (refresh) {
          const p = product()
          product({ ...p, ...updates })
        } else {
          const p = product()
          if (p) {
            if (updates.attributes) {
              p.attributes = { ...p.attributes, ...updates.attributes }
            }
            if (updates.listing_metadata) {
              p.listing_metadata = { ...p.listing_metadata, ...updates.listing_metadata }
            }
            Object.keys(updates).forEach(key => {
              if (key !== 'attributes' && key !== 'listing_metadata') {
                p[key] = updates[key]
              }
            })
            product({ ...p })
          }
        }
      } else {
        await window.alert('Failed to update product.')
      }
    } catch (err) {
      console.error('Update error:', err)
    } finally {
      saving(false)
    }
  }

  const ensurePrintifySession = async () => {
    const statusRes = await fetch('/api/system/printify/status')
    const { status: printifyStatus, hasStoredToken } = await statusRes.json()
    if (printifyStatus === 'LOGGED_IN') return true

    const extId = extensionId()
    const hasExtension = extId && await pingExtension(extId)
    if (hasExtension) {
      try {
        await syncPrintifySession(extId)
        return true
      } catch (err) {
        await window.alert(`Could not log in to Printify: ${err.message}`)
        return false
      }
    }

    // Token is stored but failed Printify's validation check — this can happen on mobile
    // if the validation endpoint rejects the token format. Let the job try and fail fast
    // with a clear error if the token is truly expired.
    if (hasStoredToken) return true

    await window.alert('No Printify session token saved. Go to Settings → Printify Integration to add one.')
    goTo('/settings')
    return false
  }

  const handlePushToPrintify = async () => {
    const p = product()
    const isTshirt = p.product_type?.toLowerCase() === 't-shirt'
    if (isTshirt) {
      const colors = p.attributes?.selected_colors || []
      if (colors.length === 0) {
        await window.alert('Please select at least one color before pushing to Printify.')
        return
      }
      const selectedSizes = getSizesFromVariantIds(variants(), selectedVariants())
      if (selectedSizes.length === 0) {
        await window.alert('Please select at least one size before pushing to Printify.')
        return
      }
    } else if (selectedVariants().length === 0) {
      await window.alert('Please select at least one variant before pushing to Printify.')
      return
    }
    saving(true)
    try {
      if (!await ensurePrintifySession()) {
        saving(false)
        return
      }

      const ensureRes = await fetch(`/api/product-concepts/${productId}/ensure-printify-product`, { method: 'POST' })
      const ensureData = await ensureRes.json()
      if (ensureData.error) throw new Error(ensureData.error)

      const res = await fetch(`/api/product-concepts/${productId}/push-to-printify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { ...p.attributes, selected_variants: selectedVariants() },
          listing_metadata: p.listing_metadata
        })
      })
      if (res.ok) {
        // saving(false) fires on PRINTIFY_PUSH completion via handleJobUpdate
      } else {
        throw new Error('Failed to trigger push job')
      }
    } catch (err) {
      console.error('Push error:', err)
      await window.alert(`Push failed: ${err.message}`)
      saving(false)
    }
  }

  const handlePublishToEtsy = async () => {
    const p = product()
    if (!p.printify_product_id) {
      await window.alert('Product must be pushed to Printify first.')
      return
    }
    if (variantsDirty()) {
      await window.alert('You changed the colors/variants but haven\'t re-pushed to Printify yet. Click "Re-push to Printify" first so the Etsy listing matches your changes.')
      return
    }
    saving(true)
    try {
      const res = await fetch(`/api/product-concepts/${productId}/publish-to-etsy`, {
        method: 'POST'
      })
      if (res.ok) {
        await window.alert('Product successfully queued for Etsy publishing!')
        goTo(returnTo)
      } else {
        const err = await res.json()
        throw new Error(err.error || 'Failed to trigger publish job')
      }
    } catch (err) {
      console.error('Publish error:', err)
      await window.alert(`Publish failed: ${err.message}`)
    } finally {
      saving(false)
    }
  }

  const handleSync = async () => {
    saving(true)
    try {
      if (!await ensurePrintifySession()) {
        saving(false)
        return
      }
      const res = await fetch(`/api/product-concepts/${productId}/re-finalize`, { method: 'POST' })
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to trigger sync')
      // saving(false) fires on PRINTIFY_PUSH completion via handleJobUpdate
    } catch (err) {
      console.error('Sync error:', err)
      await window.alert(`Sync failed: ${err.message}`)
      saving(false)
    }
  }

  const handleUnpublish = async () => {
    if (!await window.confirm('Are you sure you want to remove this product from Etsy?')) return
    saving(true)
    try {
      const res = await fetch(`/api/product-concepts/${productId}/unpublish`, { method: 'POST' })
      if (res.ok) {
        await window.alert('Product successfully queued for unpublishing!')
        fetchProduct()
      } else {
        await window.alert('Failed to enqueue unpublish job.')
      }
    } catch (err) {
      console.error('Unpublish error:', err)
    } finally {
      saving(false)
    }
  }

  const handleReject = async () => {
    if (await window.confirm('Are you sure you want to reject this product?')) {
      handleUpdate({ status: 'FAILED' })
    }
  }

  const handleGenerateMoreMockups = async () => {
    const { MOCKUP_IMAGE_PROVIDERS } = await import('../../components/ModelSelector.js')
    const opts = await window.promptMockupOptions(mockupModel(), MOCKUP_IMAGE_PROVIDERS, 'Generate More Mockups', true)
    if (opts === null) return

    mockupModel(opts.model)

    const res = await fetch(`/api/product-concepts/${productId}/generate-mockups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ count: mockupCount(), additional_instructions: opts.instructions, image_model: opts.model, framed_artwork: opts.framedArtwork })
    })
    if (res.ok) {
      // Optimistic placeholders show spinners INSTANTLY (before the worker — which may
      // be queued behind autopilot jobs — creates the real PENDING rows). fetchProduct
      // preserves them until the server's real rows catch up, and their PENDING status
      // keeps the poll alive so it can't quit before the rows appear.
      const cur = mockupsList()
      optimisticBaseline = cur.filter(m => !isOptimistic(m)).length
      const placeholders = Array.from({ length: mockupCount() }, (_, i) => ({ id: `pending-${Date.now()}-${i}`, status: 'PENDING', type: 'MOCKUP' }))
      mockupsList([...cur, ...placeholders])
      startMockupPolling()
    } else {
      await window.alert('Failed to trigger mockup generation.')
    }
  }

  const handleEnhance = async (asset) => {
    const isMasking = window.__isMasking()
    const correction = await window.prompt(isMasking ? 'Any specific corrections for the masked area? (optional):' : 'Any specific enhancements for the whole mockup? (optional):')
    if (correction === null) return

    const originalImageBlob = await fetch(`/api/image?file=${encodeURIComponent(asset.file_path)}`).then(r => r.blob())

    const formData = new FormData()
    if (isMasking) {
      const maskBlob = await window.__getMaskBlob()
      formData.append('mask', maskBlob, 'mask.png')
    }
    formData.append('original', originalImageBlob, 'original.jpg')
    formData.append('user_correction', correction)

    const res = await fetch(`/api/assets/${asset.id}/enhance`, {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      if (isMasking) window.__toggleMasking()
      selectedAsset(null)
      await window.alert('Enhancement enqueued!')
    } else {
      await window.alert('Failed to trigger enhancement.')
    }
  }

  const toggleMockupReject = async (mockupId, currentlyRejected) => {
    if (currentlyRejected || await window.confirm('Reject this mockup?')) {
      const res = await fetch(`/api/assets/${mockupId}/${currentlyRejected ? 'unreject' : 'reject'}`, {
        method: 'POST'
      })
      if (res.ok) {
        fetchProduct()
      }
    }
  }

  fetchProduct()

  // Sibling tab strip: one tab per concept on this artwork, labelled by product
  // type, the open one highlighted. Hidden until there is more than one. Each
  // tab is its own route, so clicking rebuilds the page for that concept.
  const siblingLabel = (productType) => {
    const key = (productType || '').toLowerCase()
    if (key === 'canvas wall art') return 'Canvas'
    if (key === 'framed canvas wall art') return 'Framed Canvas'
    return key.replace(/\b\w/g, c => c.toUpperCase())
  }
  const fromQuery = returnTo !== '/product-review' ? `?from=${encodeURIComponent(returnTo)}` : ''
  const siblingStrip = div({
    role: 'tablist',
    class: siblings.bindAttr(list => list.length > 1 ? 'tabs tabs-box tabs-sm w-fit mb-4' : 'hidden')
  })
  siblings.bindChildren(siblingStrip, sibling => {
    const s = sibling()
    return a({
      role: 'tab',
      class: s.is_current ? 'tab tab-active gap-2' : 'tab gap-2',
      title: `${s.product_type} (${s.status})`,
      onclick: () => { if (!s.is_current) goTo(`/product-review/${s.id}${fromQuery}`) }
    },
    siblingLabel(s.product_type),
    span({ class: 'badge badge-ghost badge-xs opacity-60' }, s.etsy_listing_id ? 'Live' : (s.status || '').toLowerCase().replace(/_/g, ' '))
    )
  })

  rootEl = div({ class: 'p-4 md:p-6 flex flex-col min-h-[calc(100vh-64px)] lg:min-h-0 lg:h-[calc(100vh-120px)] lg:overflow-hidden' },
    ImageViewer(viewerAsset, null, null, (asset) => asset.type === 'UPSCALE'
      ? []
      : [
          window.__isMasking.bindAs(m => m
            ? button({
              class: 'btn btn-error btn-sm',
              onclick: () => window.__toggleMasking()
            }, 'Cancel')
            : button({
              class: 'btn btn-ghost text-white btn-sm',
              onclick: () => window.__toggleMasking()
            }, 'Paint Mask')
          ),
          CreditButton({
            actionCode: 'IMAGE_ENHANCE',
            class: 'btn btn-primary btn-sm',
            onclick: async () => handleEnhance(asset),
            label: window.__isMasking.bindAs(m => m ? 'Apply Mask & Enhance' : 'Enhance')
          })
        ]),
    div({ class: 'flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6' },
      div({ class: 'flex items-center gap-4' },
        button({ class: 'btn btn-circle btn-ghost', onclick: () => goTo(returnTo) }, '←'),
        h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Product Review Detail'),
        // Pin protects a product from autonomous tombstoning (never auto-retired).
        product.bindAs(p => p
          ? button({
            class: p.is_pinned ? 'btn btn-sm btn-warning gap-1' : 'btn btn-sm btn-ghost gap-1',
            title: p.is_pinned ? 'Pinned — protected from auto-retirement. Click to unpin.' : 'Pin to protect from auto-retirement',
            disabled: saving.bindAttr(s => s),
            onclick: () => handleUpdate({ is_pinned: !product().is_pinned })
          }, span({ class: 'material-symbols-rounded text-base' }, p.is_pinned ? 'push_pin' : 'push_pin'), p.is_pinned ? 'Pinned' : 'Pin')
          : span())
      ),
      PublishControls({
        product,
        saving,
        variantsDirty,
        handlePushToPrintify,
        handlePublishToEtsy,
        handleSync,
        handleUnpublish,
        handleReject
      })
    ),
    siblingStrip,
    product.bindAs(p => {
      if (!p) return div({ class: 'flex items-center justify-center h-64' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))

      // T-shirt color selection: redirect to the canonical product-init page
      const availableColors = p.attributes?.available_colors || []
      if (p.status === 'AWAITING_COLOR_SELECTION' && availableColors.length > 0) {
        goTo(`/product-init/${productId}`)
        return div()
      }

      if (!p.upscaled_image) {
        return div({ class: 'flex flex-col items-center justify-center gap-6 py-24 flex-1' },
          span({ class: 'loading loading-spinner loading-lg text-primary' }),
          div({ class: 'text-center space-y-2' },
            div({ class: 'font-bold text-lg' }, 'Upscaling master artwork...'),
            div({ class: 'text-sm opacity-50' }, 'This usually takes about 60 seconds. The page will update automatically.')
          )
        )
      }

      const upscaledAsset = { id: p.upscaled_image_id, file_path: p.upscaled_image, type: 'UPSCALE' }
      const sourceThumbnail = p.bg_removed_file_path || p.source_asset_path || p.upscaled_image

      return div({ class: 'flex flex-col lg:flex-row gap-6 items-start lg:items-stretch lg:flex-1 lg:min-h-0' },
        MockupPanel({
          mockupsList,
          mockupCount,
          selectedAsset,
          viewerAsset,
          product,
          showRejectedMockups,
          handleUpdate,
          handleGenerateMoreMockups,
          toggleMockupReject
        }),
        MetadataEditor({
          product,
          variants,
          selectedVariants,
          productTypes,
          upscaledAsset,
          bgRemovedPath: p.bg_removed_file_path,
          sourceThumbnail,
          viewerAsset,
          handleUpdate
        })
      )
    })
  )
  return rootEl
}
